<script setup>
import {computed, defineProps, ref} from 'vue';
import {getActiveLanguage, trans} from "laravel-vue-i18n";
import LogoEvalaudit from '@/Svgs/LogoEvalaudit.svg.vue';
import HelpIcon from '@/Svgs/HelpIcon.svg.vue';
import FeatherIcon from '@/Svgs/FeatherIcon.svg.vue';
import SettingsIcon from '@/Svgs/SettingsIcon.svg.vue';
import NotificationIcon from '@/Svgs/NotificationIcon.svg.vue';
import BurgerMenuIcon from '@/Svgs/BurgerMenuIcon.svg.vue';
import ListBullet from '@/Svgs/Typography/ListBullet.svg.vue';
import {Link, usePage} from "@inertiajs/vue3";
import BurgerMenu from "@/Components/BurgerMenu.vue";
import Dropdown from "@/Components/Dropdown.vue";
import DefaultModal from "@/Components/DefaultModal.vue";
import helpText from "@/../../public/data/help.json";
import FrenchFlagIcon from "@/Svgs/Flags/French.svg.vue";
import EnglishFlagIcon from "@/Svgs/Flags/English.svg.vue";
import LanguagesSwitcher from "@/Components/LanguagesSwitcher.vue";
import Notifications from "@/Components/Notifications.vue";

const props = defineProps({
    isRequiredAuthCode: {
        type: Boolean,
        required: true
    },
    user: {
        type: Object,
        default: null
    }
})

const page = usePage()
const isAdmin = computed(() => page.props.auth.isAdmin)
const routeName = computed(() => page.props.routeName)
const notificationItems = computed(() => page.props.notifications)
const openBurgerMenu = ref(false)
const isDropdownOpen = ref(false)
const isDark = ref(false)
const clickPosition = ref({ left: 0, width: 0 })
const isLanguagesSwitcherOpen = ref(false)

const openLanguagesSwitcher = (event) => {
    isLanguagesSwitcherOpen.value = true
}
const openDropdown = (event, isDarkMode = false) => {
    const target = event.target
    const rect = target.getBoundingClientRect()
    clickPosition.value = { left: rect.left, width: rect.width }
    isDark.value = isDarkMode
    isDropdownOpen.value = true
    isNotificationsDropdownOpen.value = false
}

// Notifications dropdown
const isNotificationsDropdownOpen = ref(false)
const openNotificationsDropdown = (event) => {
    const target = event.target
    const rect = target.getBoundingClientRect()
    clickPosition.value = { left: rect.left, width: rect.width }
    isNotificationsDropdownOpen.value = true
    isDropdownOpen.value = false
}

const closeNotificationsDropdown = () => {
    isNotificationsDropdownOpen.value = false
    isDropdownOpen.value = false
}

const closeBurgerMenu = () => {
    openBurgerMenu.value = false
}

const closeDropdown = () => {
    isDropdownOpen.value = false
    isNotificationsDropdownOpen.value = false
}

const helpModalIsOpen = ref(false)
const textHelpModal = ref(null)
const routeHaveHelpText = ['dashboard', 'evaluations.index', 'evaluations.scorecard', 'evaluations.show', 'repositories.index', 'third-parties.index', 'users.index']
const openModal = (context) => {
    if (context === 'dashboard') {
        textHelpModal.value = helpText.dashboard
    } else if (context === 'evaluations.index') {
        textHelpModal.value = helpText.evaluations
    } else if (context === 'evaluations.show' || context === 'evaluations.scorecard') {
        textHelpModal.value = helpText.evaluation
    } else if (context === 'repositories.index') {
        textHelpModal.value = helpText.repositories
    } else if (context === 'third-parties.index') {
        textHelpModal.value = helpText.third_parties
    } else if (context === 'users.index') {
        textHelpModal.value = helpText.users
    }

    helpModalIsOpen.value = true
}

</script>

<template>
    <header class="header">
        <div class="header-container container">
            <div class="header-menu" v-if="user && ! isRequiredAuthCode">
                <BurgerMenuIcon
                    @click.stop="openBurgerMenu = true"
                    class="-is-clickable"
                />
            </div>
            <div class="header-logo">
                <Link
                    :href="route('dashboard')"
                    class="loginPage-forgotPassword text-center"
                >
                    <LogoEvalaudit />
                </Link>
            </div>
            <template v-if="user && ! isRequiredAuthCode">
                <Link
                    :href="route('users.show')"
                    class="header-userInfo"
                >
                    <span class="header-userInfo-avatar">
                        <img :src="user.thumbnail ? '/storage/' + user.thumbnail : 'https://avatar.iran.liara.run/public/boy'" :alt="trans('shared.layout.profil-avatar')" width="38" height="38">
                    </span>
                    <span>{{ user.name }}</span>
                </Link>
                <div class="header-quickAccess">
                    <button
                        v-if="routeHaveHelpText.indexOf(routeName) !== -1"
                        class="header-quickAccess-item"
                        @click.prevent="openModal(routeName)"
                    >
                        <HelpIcon />
                        <span v-text="trans('shared.layout.help')" />
                    </button>
                    <div class="header-quickAccess-item">
                        <FeatherIcon />
                        <span>Annexe</span>
                    </div>
                    <button
                        class="header-quickAccess-item -is-clickable"
                        @click.stop="openDropdown($event)"
                    >
                        <SettingsIcon />
                        <span v-text="trans('shared.layout.settings')" />
                    </button>
                    <button
                        class="header-quickAccess-item -is-clickable"
                        @click.stop="openNotificationsDropdown($event)"
                    >
                        <NotificationIcon />
                        <span v-text="trans('shared.layout.notifications')" />
                        <span
                            v-if="notificationItems.length > 0"
                            class="header-quickAccess-item-bubble"
                            v-text="notificationItems.length"
                        ></span>
                    </button>
                </div>
                <Dropdown
                    :is-open="isDropdownOpen"
                    :is-dark="isDark"
                    :clickPosition="clickPosition"
                    @close="closeDropdown"
                >
                    <template #title>
                        <span v-text="trans('shared.layout.settings')" />
                    </template>
                    <template #body>
                        <ul
                            class="header-dropdownSettings"
                        >
                            <li v-if="isAdmin">
                                <Link :href="route('repositories.index')">
                                    <ListBullet />
                                    {{ trans('shared.layout.repository-settings') }}
                                </Link>
                            </li>
                            <li v-if="isAdmin">
                                <Link :href="route('users.index')">
                                    <ListBullet />
                                    {{ trans('shared.layout.access-user-accounts') }}
                                </Link>
                            </li>
                            <li v-if="isAdmin">
                                <Link :href="route('tags.index')">
                                    <ListBullet />
                                    {{ trans('shared.layout.tags-settings') }}
                                </Link>
                            </li>
                            <li>
                                <Link
                                    :href="route('logout')"
                                    method="post"
                                    as button
                                    class="-is-clickable"
                                >
                                    <ListBullet />
                                    {{ trans('shared.layout.logout') }}
                                </Link>
                            </li>
                        </ul>
                    </template>
                </Dropdown>
                <Notifications
                    :is-open="isNotificationsDropdownOpen"
                    :click-position="clickPosition"
                    @close="closeNotificationsDropdown"
                />
            </template>
            <div :class="['header-languageSwitcher', {'-is-alignRight': ! user || isRequiredAuthCode}]">
                <div
                    :class="['header-languageSwitcher-active -is-clickable', {'-is-open': isLanguagesSwitcherOpen}]"
                    @click.stop="openLanguagesSwitcher($event)"
                    @close="isLanguagesSwitcherOpen = false"
                >
                    <template v-if="getActiveLanguage() === 'en'">
                        <EnglishFlagIcon />
                        <span>English</span>
                    </template>
                    <template v-else>
                        <FrenchFlagIcon />
                        <span>Français</span>
                    </template>
                </div>
            </div>
            <LanguagesSwitcher
                :is-open="isLanguagesSwitcherOpen"
                @close="isLanguagesSwitcherOpen = false"
            />
        </div>
    </header>
    <BurgerMenu
        :is-open="openBurgerMenu"
        @close="closeBurgerMenu"
    />
    <DefaultModal
        v-model:open="helpModalIsOpen"
        :can-close="true"
        :has-actions="false"
    >
        <template #title>
            {{ trans('shared.layout.help') }}
        </template>

        <template #body>
            <div v-html="textHelpModal" />
        </template>
    </DefaultModal>
</template>

<style lang="scss" scoped>
@use 'include-media/dist/include-media' as *;
@use '~@/base/variables' as *;

.header {
    padding: 57px 0;

    @include media($breakpoint-phone) {
        padding: 25px 0;
    }
    &-container {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;

        @include media($breakpoint-phone) {
            justify-content: space-between;
        }
    }
    &-menu {
        margin-left: -70px;
        margin-right: 47px;

        @include media('<=1367px') {
            margin-left: 0;
            margin-right: 20px;
        }
    }
    &-userInfo {
        display: flex;
        align-items: center;
        margin-left: auto;
        background-color: $gray300;
        border-radius: 25px;
        padding: 4px 24px 4px 4px;
        gap: 16px;
        font-weight: 600;
        text-decoration: none;
        color: $blue800;

        @include media($breakpoint-phone) {
            padding: 4px;

            span {
                display: none;
            }
        }
        &-avatar {
            border-radius: 50%;
            overflow: hidden;

            img {
                display: block;
            }
        }
    }
    &-quickAccess {
        display: flex;
        align-items: center;
        margin-left: 22px;
        gap: 18px;

        @include media($breakpoint-phone) {
            position: fixed;
            background-color: white;
            bottom: 0;
            left: 0;
            width: 100%;
            margin-left: 0;
            padding: 15px 0;
            justify-content: center;
            z-index: 11;
        }
        &-item {
            min-width: 70px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 12px;
            font-size: 12px;
            cursor: pointer;
            background-color: transparent;
            padding: 0;
            border: none;
            position: relative;

            @include media($breakpoint-phone) {
                span:not(.header-quickAccess-item-bubble) {
                    display: none;
                }
            }
            &-bubble {
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                background-color: $blue200;
                border-radius: 50%;
                width: 16px;
                height: 16px;
                font-size: 11px;
                font-weight: 900;
                line-height: 1;
                position: absolute;
                top: -3px;
                left: 50%;
            }
        }
    }
    &-dropdownSettings {
        margin: 0;
        padding: 15px 30px;

        li {
            list-style: none;
            margin-bottom: 15px;

            a,
            button {
                color: $blue800;
                text-decoration: none;
                display: flex;
                align-items: center;
                gap: 15px;
                padding: 0;
                background-color: transparent;
                border: none;

                svg {
                    transition: all 0.3s ease;
                }
                &:hover {
                    svg {
                        transform: translateX(3px);
                    }
                }
            }
        }
    }
    &-dropdownLanguages {
        padding: 15px 30px;

        a {
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 10px;
            color: $blue800;
            font-weight: 500;
        }
    }
    &-languageSwitcher {

        &.-is-alignRight {
            margin-left: auto;
        }
        &-active {
            display: flex;
            align-items: center;
            gap: 10px;
            background-color: $gray300;
            border-radius: 20px;
            padding: 15px;
            margin-left: 22px;
            min-width: 130px;
            justify-content: flex-end;

            @include media($breakpoint-phone) {
                min-width: auto;
            }

            &.-is-open {
                border-bottom-left-radius: 0;
                border-bottom-right-radius: 0;
            }
            span {
                font-size: 14px;
                font-weight: bold;
                text-transform: uppercase;

                @include media($breakpoint-phone) {
                    display: none;
                }
            }
        }
    }
}
</style>
